import {Vector2} from "@utility/vector2";
import {ILetter, Letter} from "@game/map/letter";
import {Game} from "@game/game";
import {Tile} from "@game/map/tile";
import {EventHandler} from "@utility/eventHandler";
import {GameEvent} from "@game/gameEvent";
import {Place} from "@game/map/place";
import {WorkerClient} from "@host/services/workerClient";

export interface IRegionData {
    regionId: number;
    letters:  Record<string, ILetter>;
    places:   Record<string, string>;
}

export interface IRegion {
    game: Game
    regionId: number
}

export class Region {
    private readonly _events = new EventHandler<GameEvent>();
    private readonly _position: Vector2;

    private _letters: Record<number, Letter> = {};
    private _places: Record<number, Place> = {};
    private _tiles: Tile[] = [];
    private _updatePlacesChecksum = "";
    private _updateLetterChecksum = "";

    constructor(private props: IRegion) {
        this._position = props.game.size.getRegionPosition(props.regionId);
    }

    get events() {
        return this._events;
    }

    async initialise() {
        const placed: Record<number, Letter> = Object.fromEntries(this.props.game.placed
            .filter(placed => placed.regionId === this.regionId)
            .map(placed => [placed.tileId, placed.letter]));

        this._tiles = this.props.game.geography.getRegionTiles(this).map(data => {
            const tile = new Tile(data);

            if (placed[tile.tileId]) {
                tile.updateLetter(placed[tile.tileId]);
            }
            return tile;
        });

        const data = await WorkerClient.instance.get<IRegionData>(`/game/region/${this.regionId}`);

        this.handleUpdate(data);
    }

    placeLetter(letter: Letter, tileId: number) {
        const tile = this._tiles.find(tile => tile.tileId === tileId);
        
        if (tile) {
            tile.updateLetter(letter);
            this._events.update(new GameEvent("TilePlaced", {letter, tileId, region: this}))
        }
    }
    destroy() {
        // No-op: polling handled externally by DataSubscriptionManager
    }

    /** Called by DataSubscriptionManager on each poll cycle. */
    updateFromPoll(data: IRegionData): void {
        this.handleUpdate(data);
    }

    get letters() {
        return this._letters;
    }

    get places() {
        return this._places;
    }

    get position() {
        return this._position;
    }
    
    get regionId() {
        return this.props.regionId;
    }

    get tiles() {
        return this._tiles;
    }

    private handleUpdate(data: IRegionData): void {
        const updatedLetters = this.updateLetters(data.letters ?? {});
        const updatedPlaces  = this.updatePlaces(data.places   ?? {});

        if (!updatedPlaces && !updatedLetters) return;
        this.updateTiles();
        if (updatedLetters) {
            const playerIds = Object.values(this._letters)
                .map(l => l.player)
                .filter((p): p is string => p !== null && this.props.game.getNickname(p) === undefined);
            if (playerIds.length > 0) {
                this.props.game.getNicknames(Array.from(new Set(playerIds))).catch(console.error);
            }
        }
        this.events.update(new GameEvent("Updated", {region: this}));
    }

    private updatePlaces(update: Record<string, string>) {
        const checksum = JSON.stringify(update);

        if (this._updatePlacesChecksum === checksum) return false;
        this._places = Object.fromEntries(Object.entries(update)
            .map(([tile, type]) => {

                return [tile, new Place({type})];
            }));
        this._updatePlacesChecksum = checksum;
        return true;
    }

    private updateLetters(update: Record<string, ILetter>) {
        const checksum = JSON.stringify(update);

        if (this._updateLetterChecksum === checksum) return false;
        this._letters = Object.fromEntries(Object.entries(update)
            .map(([tile, letter]) => {
                return [tile, new Letter(letter)]
            }));
        this._updateLetterChecksum = checksum;
        return true;
    }
    
    private updateTiles() {
        this._tiles.forEach(tile => {
            const serverLetter = this.letters[tile.tileId];
            // Don't wipe a client-placed letter — the server hasn't committed it yet.
            // getIsPlaced() is only true for letters placed via letter.placeOnMap().
            if (tile.letter !== serverLetter && !(serverLetter === undefined && tile.letter?.getIsPlaced())) {
                tile.updateLetter(serverLetter);
            }
            
            const place = this.places[tile.tileId];

            if (place && tile.place !== place) {
                const placeImage = this.props.game.geography.getPlaceImage(tile, place);

                tile.updatePlace(place, placeImage);
            }
        });
    }
}